import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../../constants/common'
import {
  useAsyncCallback,
  useInlineNotifications,
  useSelection,
} from '../../../../hooks'
import {
  TABLE_MODAL_DEFAULT_PAGINATION,
  TABLE_MODAL_NOTIFICATION_ERROR_ID,
  TABLE_MODAL_PAGINATION_PAGE_SIZE_KEY,
} from '../../TableModal.constants'
import { useTableModalSearch } from '../useTableModalSearch'

import { getTableModalLoadingDataRequestParams } from './useTableModalLoadingData.utils'

export function useTableModalLoadingData({
  defaultPagination = TABLE_MODAL_DEFAULT_PAGINATION,
  entityId,
  getAllItemsFromResponse,
  getItemsFromResponse,
  getRequestParams = getTableModalLoadingDataRequestParams,
  initialNotifications,
  initialSelection,
  loadAllItems,
  loadItems,
  paginationPageSizeKey = TABLE_MODAL_PAGINATION_PAGE_SIZE_KEY,
  tableRowActionsConfig,
  onAfterLoadItems,
}) {
  const { t } = useTranslation()

  const {
    handleAddNotification,
    handlePrependNotification,
    handleRemoveNotification,
    notifications,
  } = useInlineNotifications({
    initialNotifications,
  })

  const [initiated, setInitiated] = useState(false)
  const [isInitiatedError, setInitiatedError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [hasItemsFromInitial, setHasItemsFromInitial] = useState(false)
  const [ordering, setOrdering] = useState(EMPTY_LIST)
  const [initialSelectionValues, setInitialSelectionValues] = useState(
    initialSelection || EMPTY_LIST
  )
  const [prevInitialSelection, setPrevInitialSelection] =
    useState(initialSelection)
  const [pagination, setPagination] = useState(defaultPagination)
  const [tableItems, setTableItems] = useState(null)
  const [tableSearch, setTableSearch] = useState(EMPTY_STRING)
  const [allItems, setAllItems] = useState(null)

  // Sync the applied initial selection during render when the prop changes; the
  // selection bookkeeping runs in the effect below.
  if (initialSelection !== prevInitialSelection) {
    setPrevInitialSelection(initialSelection)

    if (initialSelection) {
      setInitialSelectionValues(initialSelection)
    }
  }

  const {
    checkIsItemSelected,
    exclude,
    excludeIds,
    handleExclude,
    handleInitializeSelection,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleUnselectAllPages,
    isAllSelected,
    isRemoveSelection,
    selectAllPages,
    selected,
    selectedIds,
  } = useSelection({
    config: tableRowActionsConfig,
    data: tableItems,
    pagination,
  })

  function handleLoadItems({
    callback,
    initial,
    orderingParams = ordering,
    paginationParams = pagination,
    searchParam = tableSearch,
  } = EMPTY_OBJECT) {
    setLoading(true)
    setInitiatedError(false)

    const requestParams = getRequestParams({
      ordering: orderingParams,
      page: paginationParams.page,
      pageSize: paginationParams.pageSize,
      paginationPageSizeKey,
      search: searchParam,
    })

    return loadItems?.(entityId, requestParams)
      .then(async ({ data }) => {
        handleRemoveNotification(TABLE_MODAL_NOTIFICATION_ERROR_ID)

        const pageCount = data.meta?.page_count || data.pagination?.page_count

        setTableItems(getItemsFromResponse?.(data))
        setPagination(prevPagination => ({
          ...prevPagination,
          count: data.meta?.count || data.pagination?.total,
          pageCount,
          pageSize: data.meta?.limit || data.pagination?.size,
        }))

        await callback?.()

        if (initial) {
          setInitiated(true)

          setHasItemsFromInitial(
            data.meta?.total > 0 || data.pagination?.total > 0
          )
        }

        // Removing items can leave the requested page out of range (e.g.
        // disconnecting all rows on the last page) — reload the last page
        // that still has data
        if (pageCount >= 1 && paginationParams.page > pageCount) {
          setPagination(prevPagination => ({
            ...prevPagination,
            page: pageCount,
          }))

          return handleLoadItems({
            orderingParams,
            paginationParams: { ...paginationParams, page: pageCount },
            searchParam,
          })
        }

        return data
      })
      .catch(() => {
        if (initial || !initiated) {
          setInitiatedError(true)
        } else {
          handleAddNotification({
            actions: [
              {
                callback: handleLoadItems,
                text: t('Notification.RefreshButtonTextKey'),
              },
            ],
            id: TABLE_MODAL_NOTIFICATION_ERROR_ID,
            text: t('Notification.ModalDataErrorTitleKey'),
            type: NOTIFICATION_TYPE.error,
          })
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    handleLoadItems({
      callback: onAfterLoadItems,
      initial: true,
      paginationParams: pagination,
    })
  }, [loadItems])

  useEffect(() => {
    loadAllItems?.().then(({ data }) => {
      setAllItems(getAllItemsFromResponse(data))
    })
  }, [])

  useEffect(() => {
    if (initialSelection) {
      handleInitializeSelection(initialSelection)
    }
  }, [initialSelection])

  const { handleChangeSearchValue, handleClearSearch, search } =
    useTableModalSearch({
      defaultPagination,
      paginationParams: pagination,
      setPagination,
      onChange: setTableSearch,
      onLoadItems: handleLoadItems,
    })

  function handleChangePagination(nextPagination) {
    setPagination(nextPagination)
    handleLoadItems({
      orderingParams: ordering,
      paginationParams: nextPagination,
    })
  }

  const handleSetOrdering = useAsyncCallback(
    ordering => {
      setOrdering(ordering)

      return handleLoadItems({ orderingParams: ordering })
    },
    [handleLoadItems]
  )

  function handleSaveInitialSelection(values) {
    setInitialSelectionValues(values)

    handleInitializeSelection(values)
  }

  return {
    allItems,
    canShowSearchInput: hasItemsFromInitial,
    checkIsItemSelected,
    exclude,
    excludeIds,
    handleAddNotification,
    handleChangePagination,
    handleChangeSearchValue,
    handleClearSearch,
    handleExclude,
    handleLoadItems,
    handlePrependNotification,
    handleRemoveNotification,
    handleSaveInitialSelection,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleSetOrdering,
    handleUnselectAllPages,
    initialSelectionValues,
    initiated,
    isAllSelected,
    isInitiatedError,
    isInitLoading: !initiated && loading,
    isRemoveSelection,
    loading,
    notifications,
    ordering,
    pagination,
    search,
    selectAllPages,
    selected,
    selectedIds,
    setLoading,
    tableItems,
  }
}
